/**
 * Per-type bubble registry.
 *
 * The dispatcher (`MessageBubble`) cannot import every subcomponent
 * directly, because each subcomponent imports the dispatcher's
 * `BubbleSubcomponentProps` shape — a direct edge in both directions would
 * be a circular import. Instead, each per-type subcomponent calls
 * `registerBubble(...)` at module scope and the dispatcher reads the
 * registry at render time. The barrel (`./index.ts`) imports each
 * subcomponent for its side-effect so registration runs before the first
 * dispatcher render.
 *
 * `ChatMessageType.reply` (index 4) is intentionally absent — Flutter falls
 * through to `Container()` (`chat_screen.dart:1393` / `:2505`) and so do we.
 * Any type not in the registry returns `null` from the dispatcher.
 */

import type React from 'react';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { ReadReceiptMode } from '@/domain/enums/chat/readReceiptMode';

/**
 * Props every per-type bubble subcomponent receives from the dispatcher.
 *
 * Subcomponents (TextBubble, GiftBubble, …) MUST take exactly this shape —
 * the registry is typed against it.
 *
 * Note on neighbor-aware data: the dispatcher does NOT pass the whole reversed
 * `messages` array or this bubble's `index`. Anything a leaf would have
 * computed by walking neighbors (corner-radii via `isLastMessageRight` /
 * `isLastMessageLeft`, read-receipt mode via the `myLastReadPosition` walk)
 * is precomputed ONCE in `ChatMessagesList.renderItem` and threaded down as a
 * primitive (`isLastOfRun`, `readReceiptMode`). This keeps each leaf's render
 * input scoped to its own data so React can short-circuit per-message renders.
 */
export interface BubbleSubcomponentProps {
  /** The message to render. */
  message: ChatMessageEntity;
  /**
   * Peer user. Bubbles that need the peer name (gift, share, blocked
   * overlays) read it from here. Passed through even for own bubbles so
   * subcomponents have a single shape.
   */
  peerUser: UserEntity | null;
  /** Current Firebase user id. */
  currentUserId: string;
  /** True iff `message.idFrom === currentUserId`. Precomputed by the dispatcher. */
  isOwn: boolean;
  /**
   * True iff this bubble is the run-end of its sender (Flutter's
   * `isLastMessageRight` for own / `isLastMessageLeft` for peer). Precomputed
   * once per render by `ChatMessagesList`. The corner-radii helper uses this
   * directly.
   */
  isLastOfRun: boolean;
  /**
   * Precomputed read-receipt mode for own bubbles (one of `failed` /
   * `notYetSeen` / `lastReadAvatar` / `spacer`). `MessageBubble` forwards
   * this into `ReadReceiptAvatar`. Peer bubbles never render a receipt; the
   * value is still threaded down so the prop shape stays uniform across
   * subcomponents (matches the same uniformity rule used for `onRetry` —
   * only TextBubble fires it but every subcomponent receives it).
   */
  readReceiptMode: ReadReceiptMode;
  /**
   * Whether the timestamp is currently expanded for THIS message. The hook
   * keeps "only one open at a time" globally; this prop is the resolved
   * answer for this specific bubble.
   */
  isTimestampOpen: boolean;
  /**
   * Toggle the timestamp for the given message's `timestamp` key. The hook
   * forces every other entry to `false` (matches Flutter's
   * `_messageTimeSeen.forEach` reset, `chat_screen.dart:1405-1409`).
   */
  onToggleTimestamp: (timestamp: number) => void;
  /**
   * Retry the failed send. Wired only for own text bubbles when
   * `message.success === false` (`chat_screen.dart:1400-1403`). Pass it
   * through to every subcomponent for uniformity — only TextBubble fires it.
   */
  onRetry: (message: ChatMessageEntity) => void;
  /**
   * Join an in-progress video call as the audience. Wired only for peer
   * `videoCall` bubbles that are LIVE (`message.isLive === true`) — the
   * audience-join entry point (Flutter `chat_screen.dart:2899-2924`). The page
   * owns the confirm sheet + permission-gated launch because the bubble lacks
   * the full `currentUser` entity + `groupChatId` the launch needs. Threaded
   * through to every subcomponent for uniformity (same as `onRetry`) — only
   * VideoCallBubble fires it.
   */
  onVideoCallJoin?: (message: ChatMessageEntity) => void;
  /**
   * Screen key of the hosting page — threaded into `InfoSheet` calls by
   * the subcomponents that stub out to `InfoSheet.showNotImplementedYet`.
   */
  screenKey: string;
}

const BUBBLE_REGISTRY: Partial<
  Record<ChatMessageType, React.ComponentType<BubbleSubcomponentProps>>
> = {};

/**
 * Internal API used by per-type bubble subcomponents to register
 * themselves with the dispatcher. Each subcomponent file calls this once
 * at module scope so the dispatcher does not need to import every
 * subcomponent directly (which would create a circular import — see the
 * module docblock).
 *
 * Example:
 *
 * ```ts
 * // TextBubble.tsx
 * import {
 *   registerBubble,
 *   BubbleSubcomponentProps,
 * } from '../../registry/bubbleRegistry';
 * const TextBubble: React.FC<BubbleSubcomponentProps> = (props) => { ... };
 * registerBubble(ChatMessageType.text, TextBubble);
 * ```
 *
 * Calling `registerBubble` more than once for the same type overrides the
 * previous registration — useful for testing.
 */
export function registerBubble(
  type: ChatMessageType,
  component: React.ComponentType<BubbleSubcomponentProps>,
): void {
  BUBBLE_REGISTRY[type] = component;
}

/**
 * Returns the registered subcomponent for `type`, or `undefined` if none.
 * Exported mainly for tests; the dispatcher itself reads the registry
 * directly via this same helper.
 */
export function getRegisteredBubble(
  type: ChatMessageType,
): React.ComponentType<BubbleSubcomponentProps> | undefined {
  return BUBBLE_REGISTRY[type];
}
